// components/profile/ActivityViewModal.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { X, Edit2, Trash2, Save, XCircle, Award } from "lucide-react";
import {
  updateActivity,
  deleteActivity as deleteActivityThunk,
} from "@/store/slices/activitySlice";
import { routesAPI } from "@/services/routes";
import RouteDetailMap from "@/components/map/RouteDetailMap";
import Modal from "../common/Modal";
import Button from "../common/Button";
import TextInput from "../common/TextInput";
import DatePicker from "../common/DatePicker";

const ActivityViewModal = ({ activity, open, onClose }) => {
  const dispatch = useDispatch();
  const { submitLoading, activities } = useSelector((state) => state.activity);

  const [editMode, setEditMode] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    distance: "",
    duration: "",
    completed_at: "",
  });

  // Get the current activity from Redux store (to reflect updates)
  const currentActivity =
    activities.find((a) => a._id === activity?._id) || activity;

  // Initialize edit form when activity changes
  useEffect(() => {
    if (currentActivity) {
      setEditForm({
        title: currentActivity.title || "",
        distance: currentActivity.distance || "",
        duration: currentActivity.duration || "",
        completed_at: currentActivity.completed_at
          ? new Date(currentActivity.completed_at).toISOString().split("T")[0]
          : "",
      });
    }
  }, [currentActivity]);

  // Fetch route data if activity has route_id
  useEffect(() => {
    if (currentActivity?.route_id && open) {
      // Handle both object and string formats
      const routeId =
        typeof currentActivity.route_id === "object"
          ? currentActivity.route_id._id
          : currentActivity.route_id;

      setLoadingRoute(true);
      routesAPI
        .getRouteById(routeId)
        .then((data) => {
          setRouteData(data);
        })
        .catch((err) => {
          console.error("Failed to fetch route:", err);
          toast.error("Failed to load route data");
        })
        .finally(() => setLoadingRoute(false));
    } else {
      setRouteData(null);
    }
  }, [currentActivity?.route_id, open]);

  if (!currentActivity) return null;

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    // Reset form
    setEditForm({
      title: currentActivity.title || "",
      distance: currentActivity.distance || "",
      duration: currentActivity.duration || "",
      completed_at: currentActivity.completed_at
        ? new Date(currentActivity.completed_at).toISOString().split("T")[0]
        : "",
    });
  };

  const handleSave = async () => {
    if (!editForm.title || !editForm.duration || !editForm.distance) {
      toast.warn("Please fill all required fields");
      return;
    }

    try {
      const payload = {
        title: editForm.title.trim(),
        distance: Number(editForm.distance),
        duration: Number(editForm.duration),
      };
      if (editForm.completed_at) {
        payload.completed_at = new Date(editForm.completed_at).toISOString();
      }

      await dispatch(
        updateActivity({ id: currentActivity._id, payload })
      ).unwrap();
      toast.success("Activity updated successfully!");
      setEditMode(false);
    } catch (err) {
      toast.error(err || "Failed to update activity");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this activity?"))
      return;

    try {
      await dispatch(deleteActivityThunk(currentActivity._id)).unwrap();
      toast.success("Activity deleted successfully");
      onClose();
    } catch (err) {
      toast.error(err || "Failed to delete activity");
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="">
      <div className="flex flex-col h-full max-h-[80vh]">
        {/* Header */}
        <div className="flex justify-between items-center pb-4 border-b dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
            Activity Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Split Panel Content */}
        <div className="flex-1 overflow-hidden mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
            {/* Left: Map Section */}
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-semibold dark:text-gray-200">
                  Route Map
                </h3>
                {currentActivity.route_id && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full">
                    <Award className="w-3 h-3" />
                    Official
                  </span>
                )}
              </div>

              <div className="flex-1 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 min-h-[300px]">
                {loadingRoute ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                    <p className="text-gray-500 dark:text-gray-400">
                      Loading route...
                    </p>
                  </div>
                ) : (
                  <RouteDetailMap 
                    route={routeData || {
                      coordinates: currentActivity.coordinates?.coordinates || [],
                      startPoint: currentActivity.title,
                      endPoint: currentActivity.title
                    }} 
                  />
                )}
              </div>
            </div>

            {/* Right: Details Section */}
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold mb-3 dark:text-gray-200">
                Details
              </h3>

              <div className="flex-1 overflow-y-auto space-y-4">
                {editMode ? (
                  <>
                    <TextInput
                      label="Activity Title *"
                      value={editForm.title}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, title: e.target.value }))
                      }
                      required
                    />

                    <TextInput
                      label="Distance (km) *"
                      type="number"
                      min="0"
                      step="0.01"
                      value={editForm.distance}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, distance: e.target.value }))
                      }
                      required
                    />

                    <TextInput
                      label="Duration (min) *"
                      type="number"
                      min="1"
                      value={editForm.duration}
                      onChange={(e) =>
                        setEditForm((p) => ({ ...p, duration: e.target.value }))
                      }
                      required
                    />

                    <DatePicker
                      label="Completed At"
                      value={editForm.completed_at}
                      onChange={(e) =>
                        setEditForm((p) => ({
                          ...p,
                          completed_at: e.target.value,
                        }))
                      }
                      allowFuture={false}
                    />
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                        Title
                      </label>
                      <p className="text-lg font-semibold text-gray-800 dark:text-white">
                        {currentActivity.title}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                          Distance
                        </label>
                        <p className="text-lg font-semibold text-gray-800 dark:text-white">
                          {currentActivity.distance?.toFixed(2)} km
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                          Duration
                        </label>
                        <p className="text-lg font-semibold text-gray-800 dark:text-white">
                          {currentActivity.duration} min
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                        Average Speed
                      </label>
                      <p className="text-lg font-semibold text-gray-800 dark:text-white">
                        {currentActivity.avg_speed?.toFixed(2)} km/h
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                        Completed At
                      </label>
                      <p className="text-lg font-semibold text-gray-800 dark:text-white">
                        {currentActivity.completed_at
                          ? new Date(
                              currentActivity.completed_at
                            ).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })
                          : "—"}
                      </p>
                    </div>

                    {routeData && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                          Route Name
                        </label>
                        <p className="text-lg font-semibold text-gray-800 dark:text-white">
                          {routeData.name}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-4 pt-4 border-t dark:border-gray-700">
                {editMode ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={handleCancelEdit}
                      className="flex-1"
                      disabled={submitLoading}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      loading={submitLoading}
                      disabled={submitLoading}
                      className="flex-1"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={handleEdit}
                      className="flex-1 hover:bg-green-700 dark:hover:bg-green-700"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleDelete}
                      loading={submitLoading}
                      disabled={submitLoading}
                      className="flex-1 text-red-600 border border-gray-300 hover:bg-red-50 dark:text-red-500 dark:border-white/30 dark:hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ActivityViewModal;
